import { generatePath, To } from 'react-router-dom'

import { Table, usePagination } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { calculatePagination } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useGetAbTests } from './hooks/useGetAbTests'
import { AbTestsForTable, AbTestsSortBy } from './hooks/useGetAbTests/types'
import { AbTestsTableProps } from './types'
import { useColumns } from './useColumns'

export const AbTestsTable = ({ filters }: AbTestsTableProps): React.JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { page, rowsPerPage, setPage, ...usePaginationProps } = usePagination({
    resetOnChange: [filters],
  })
  const { props: sortProps, sort } = useSort<AbTestsForTable, AbTestsSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const { abTests, count, loading } = useGetAbTests({
    filters,
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  })

  const columns = useColumns()

  const handleRowClick = (row: AbTestsForTable): To =>
    generatePath(ROUTES.AB_TESTS.EDIT, {
      id: row.id,
      workspaceId,
    })

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    ...usePaginationProps,
  }

  const canAccessAbTestsEdit: boolean = ability.can('access', 'AbTestsPage-edit')

  return (
    <Table<AbTestsForTable>
      columns={columns}
      data={abTests}
      loading={loading}
      meta={{ stickyHeader: true }}
      onClickRow={canAccessAbTestsEdit ? id => handleRowClick(id) : undefined}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
